import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Translate, I18n } from "react-redux-i18n";
import {
  FaMapMarkerAlt,
  FaExchangeAlt,
  FaCreditCard,
  FaCommentsDollar,
  FaCheck,
  FaStar,
  FaShoppingBag,
} from "react-icons/fa";
import Modal from "./Modal";
import alertify from "alertifyjs";
import { getAdDisplayImage } from "../utils/adMediaUtils";
import * as actions_address from "../redux/actions/addressActions";
import * as actions_checkout from "../redux/actions/checkoutActions";
import "./styles/ModalCheckout.css";

const KIND_SALE = 2;
const KIND_DONATION = 1;
const KIND_EXCHANGE = 3;

class ModalCheckout extends React.Component {
  state = {
    step: 0,
    selectedAddressId: null,
    payment: "",
    ready: false,
  };

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.init();
    }
  }

  init = async () => {
    this.setState({
      step: 0,
      ready: false,
      payment: "",
      selectedAddressId: null,
    });

    const { user_id, token, ad } = this.props;

    await Promise.all([
      this.props.LoadAddresses(user_id, token),
      this.props.LoadCheckout(user_id, token, ad.Id),
    ]);

    this.setState({
      ready: true,
      selectedAddressId: null,
      payment: "",
    });
  };

  getPaymentOptions() {
    const options = [];

    if (this.props.transfer) {
      options.push({
        value: "transfer",
        icon: <FaExchangeAlt />,
        name: I18n.t("payment_method_transfer"),
        desc: I18n.t("payment_transfer_desc"),
      });
    }

    if (!this.props.transfer) {
      options.push({
        value: "none",
        icon: <FaCommentsDollar />,
        name: I18n.t("payment_method_none"),
        desc: I18n.t("checkout_contact_seller_desc"),
      });
    }

    return options;
  }

  canContinue() {
    const step = this.currentStep();
    if (step === "address") {
      return Boolean(this.state.selectedAddressId);
    }
    if (step === "payment") {
      return Boolean(this.state.payment);
    }
    return true;
  }

  isSale() {
    return Number(this.props.ad.KindCommerceId) === KIND_SALE;
  }

  steps() {
    const list = ["address"];
    if (this.isSale()) list.push("payment");
    list.push("summary");
    return list;
  }

  currentStep() {
    return this.steps()[this.state.step];
  }

  selectAddress = (id) => () => {
    this.setState({ selectedAddressId: id });
  };

  selectPayment = (value) => () => {
    this.setState({ payment: value });
  };

  next = () => {
    if (this.currentStep() === "address" && !this.state.selectedAddressId) {
      alertify.warning(I18n.t("checkout_address_required_select"));
      return;
    }
    if (this.currentStep() === "payment" && !this.state.payment) {
      alertify.warning(I18n.t("checkout_payment_required_select"));
      return;
    }
    this.setState({ step: Math.min(this.state.step + 1, this.steps().length - 1) });
  };

  confirm = () => {
    if (!this.state.selectedAddressId) {
      alertify.warning(I18n.t("checkout_address_required_select"));
      return;
    }
    if (this.isSale() && !this.state.payment) {
      alertify.warning(I18n.t("checkout_payment_required_select"));
      return;
    }
    const address = (this.props.addressList || []).find(
      (a) => a.Id === this.state.selectedAddressId
    );
    this.props.onConfirm({
      addressId: this.state.selectedAddressId,
      address,
      payment: this.isSale() ? this.state.payment : "contact",
    });
  };

  renderAddressSummary(item) {
    const parts = [
      item.Address1,
      item.City && item.City.Name,
      item.State && item.State.Name,
    ].filter((v) => v && String(v).trim());
    return parts.join(", ");
  }

  getSelectedAddress() {
    return (this.props.addressList || []).find(
      (item) => item.Id === this.state.selectedAddressId
    );
  }

  getKindLabel() {
    const kind = Number(this.props.ad?.KindCommerceId);
    if (kind === KIND_DONATION) return I18n.t("donation");
    if (kind === KIND_EXCHANGE) return I18n.t("exchange");
    if (kind === KIND_SALE) return I18n.t("sale");
    return "";
  }

  getPaymentLabel() {
    if (this.state.payment === "transfer") {
      return I18n.t("payment_method_transfer");
    }
    if (this.state.payment === "none") {
      return I18n.t("payment_method_none");
    }
    return "";
  }

  getQuantity() {
    const quantity = Number(this.props.quantity);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  getUnitPrice() {
    const price = Number(this.props.ad?.Price);
    return Number.isFinite(price) ? price : 0;
  }

  getTotalPrice() {
    return this.getUnitPrice() * this.getQuantity();
  }

  formatMoney(amount) {
    const currency = this.props.ad?.Currency || "USD";
    return `${amount.toFixed(2)} ${currency}`;
  }

  renderStepper() {
    const steps = this.steps();
    return (
      <div className="checkout-stepper">
        {steps.map((s, index) => (
          <div
            key={s}
            className={
              "checkout-stepper__dot" +
              (index === this.state.step ? " checkout-stepper__dot--active" : "") +
              (index < this.state.step ? " checkout-stepper__dot--done" : "")
            }
          >
            {index + 1}
          </div>
        ))}
      </div>
    );
  }

  renderAddressStep() {
    const list = this.props.addressList || [];

    return (
      <div className="checkout-step">
        <h5 className="checkout-step__title">
          <FaMapMarkerAlt /> <Translate value="checkout_address_title" />
        </h5>
        <p className="checkout-step__hint">
          <Translate value="checkout_address_hint" />
        </p>

        {list.length === 0 ? (
          <div className="checkout-empty">
            <FaMapMarkerAlt />
            <p>
              <Translate value="checkout_address_empty" />
            </p>
            <Link to="/profile/more_data" className="checkout-empty__link">
              <Translate value="bank_account_add" />
            </Link>
          </div>
        ) : (
          <ul className="checkout-address-list">
            {list.map((item) => (
              <li key={item.Id}>
                <button
                  type="button"
                  className={
                    "checkout-address" +
                    (this.state.selectedAddressId === item.Id
                      ? " checkout-address--active"
                      : "")
                  }
                  onClick={this.selectAddress(item.Id)}
                >
                  <span className="checkout-address__radio">
                    {this.state.selectedAddressId === item.Id && <FaCheck />}
                  </span>
                  <span className="checkout-address__body">
                    <span className="checkout-address__name">
                      {item.Name || I18n.t("address_label")}
                      {item.IsPrimary && (
                        <span className="checkout-address__primary">
                          <FaStar /> <Translate value="address_primary" />
                        </span>
                      )}
                    </span>
                    <span className="checkout-address__summary">
                      {this.renderAddressSummary(item) || (
                        <Translate value="address_incomplete" />
                      )}
                    </span>
                    {item.Phone ? (
                      <span className="checkout-address__phone">{item.Phone}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {list.length > 0 && (
          <Link to="/profile/more_data" className="checkout-manage-link">
            <Translate value="checkout_address_manage" />
          </Link>
        )}

        {list.length > 0 && !this.state.selectedAddressId && (
          <p className="checkout-address-error">
            <Translate value="checkout_address_required_select" />
          </p>
        )}
      </div>
    );
  }

  renderPaymentStep() {
    const options = this.getPaymentOptions();

    return (
      <div className="checkout-step">
        <h5 className="checkout-step__title">
          <FaCreditCard /> <Translate value="checkout_payment_title" />
        </h5>
        <p className="checkout-step__hint">
          <Translate value="checkout_payment_hint" />
        </p>

        <div className="checkout-options">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={
                "checkout-option" +
                (this.state.payment === opt.value
                  ? " checkout-option--active"
                  : "")
              }
              onClick={this.selectPayment(opt.value)}
            >
              <span className="checkout-option__icon">{opt.icon}</span>
              <span className="checkout-option__body">
                <span className="checkout-option__name">{opt.name}</span>
                <span className="checkout-option__desc">{opt.desc}</span>
              </span>
              <span
                className={
                  "checkout-option__check" +
                  (this.state.payment === opt.value
                    ? " checkout-option__check--on"
                    : "")
                }
              >
                {this.state.payment === opt.value && <FaCheck />}
              </span>
            </button>
          ))}
        </div>

        {!this.state.payment && (
          <p className="checkout-address-error">
            <Translate value="checkout_payment_required_select" />
          </p>
        )}
      </div>
    );
  }

  renderSummaryStep() {
    const { ad } = this.props;
    const address = this.getSelectedAddress();
    const image = getAdDisplayImage(ad);
    const quantity = this.getQuantity();
    const showPrice = this.isSale();

    return (
      <div className="checkout-step">
        <h5 className="checkout-step__title">
          <FaShoppingBag /> <Translate value="checkout_summary_title" />
        </h5>
        <p className="checkout-step__hint">
          <Translate value="checkout_summary_hint" />
        </p>

        <div className="checkout-summary">
          <div className="checkout-summary__product">
            {image ? (
              <img
                src={image}
                alt={ad?.Name || "product"}
                className="checkout-summary__image"
              />
            ) : null}
            <div className="checkout-summary__product-body">
              <h6 className="checkout-summary__name">{ad?.Name}</h6>
              <span className="checkout-summary__type">{this.getKindLabel()}</span>
            </div>
          </div>

          <dl className="checkout-summary__details">
            <div className="checkout-summary__row">
              <dt><Translate value="order_quantity" /></dt>
              <dd>{quantity}</dd>
            </div>

            {showPrice && (
              <div className="checkout-summary__row">
                <dt><Translate value="order_price" /></dt>
                <dd>{this.formatMoney(this.getUnitPrice())}</dd>
              </div>
            )}

            {showPrice && (
              <div className="checkout-summary__row checkout-summary__row--total">
                <dt><Translate value="checkout_summary_total" /></dt>
                <dd>{this.formatMoney(this.getTotalPrice())}</dd>
              </div>
            )}

            <div className="checkout-summary__row">
              <dt><Translate value="checkout_address_title" /></dt>
              <dd>
                {address
                  ? this.renderAddressSummary(address) || I18n.t("address_incomplete")
                  : "—"}
              </dd>
            </div>

            {this.isSale() && (
              <div className="checkout-summary__row">
                <dt><Translate value="checkout_payment_title" /></dt>
                <dd>{this.getPaymentLabel()}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    );
  }

  renderBody() {
    if (!this.state.ready || this.props.loading) {
      return (
        <div className="checkout-loading">
          <Translate value="loading" />
        </div>
      );
    }

    const step = this.currentStep();
    if (step === "address") return this.renderAddressStep();
    if (step === "payment") return this.renderPaymentStep();
    return this.renderSummaryStep();
  }

  render() {
    const isLast = this.state.step === this.steps().length - 1;
    const canContinue = this.canContinue();

    return (
      <Modal
        isOpen={this.props.isOpen}
        onClose={this.props.onClose}
        title={<Translate value="checkout_title" />}
      >
        <div className="checkout">
          {this.state.ready && this.renderStepper()}
          {this.renderBody()}

          {this.state.ready && (
            <div className="checkout-actions">
              {!isLast ? (
                <button
                  type="button"
                  className="checkout-btn checkout-btn--primary"
                  onClick={this.next}
                  disabled={!canContinue}
                >
                  <Translate value="checkout_continue" />
                </button>
              ) : (
                <button
                  type="button"
                  className="checkout-btn checkout-btn--primary"
                  onClick={this.confirm}
                  disabled={
                    this.props.submitting ||
                    !this.state.selectedAddressId ||
                    (this.isSale() && !this.state.payment)
                  }
                >
                  {this.props.submitting ? "..." : I18n.t("checkout_confirm")}
                </button>
              )}
            </div>
          )}
        </div>
      </Modal>
    );
  }
}

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.access_token,
    addressList: reducer.address.list,
    loading: reducer.checkout.loading,
    paymentMethod: reducer.checkout.paymentMethod,
    transfer: reducer.checkout.transfer,
    gateway: reducer.checkout.gateway,
    bankAccounts: reducer.checkout.bankAccounts,
    contact: reducer.checkout.contact,
    seller: reducer.checkout.seller,
  };
}

const mapDispatchToProps = (dispatch) => ({
  LoadAddresses: (user_id, token) =>
    dispatch(actions_address.loadList({ user_id, token })),
  LoadCheckout: (user_id, token, goder_id) =>
    dispatch(actions_checkout.loadSellerCheckout({ user_id, token, goder_id })),
});

export default connect(mapStateToProps, mapDispatchToProps)(ModalCheckout);
